import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';
import { debugReactError130 } from './utils/debugHelper';
import './utils/errorMonitor'; // Inicializar monitor de erros

// Error Boundary melhorado para capturar erros de JavaScript
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Atualiza o state para mostrar a UI de erro
    return {
      hasError: true,
      error,
      errorCount: (this.state?.errorCount || 0) + 1
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log detalhado do erro
    console.group('🚨 Error Boundary - Erro Capturado');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Error Stack:', error.stack);
    console.error('Error Name:', error.name);
    console.error('Error Message:', error.message);
    console.groupEnd();

    // Salvar informações adicionais no state
    this.setState({
      errorInfo,
      error
    });

    // Verificar se é um erro React #130 específico e usar debug helper
    if (error.message && (error.message.includes('Element type is invalid') ||
      error.message.includes('Minified React error #130'))) {
      debugReactError130(error, errorInfo.componentStack);
    }

    // Tentar recuperar automaticamente após alguns segundos
    if (this.state.errorCount < 3) {
      setTimeout(() => {
        console.log('🔄 Tentando recuperar automaticamente...');
        this.setState({
          hasError: false,
          error: null,
          errorInfo: null
        });
      }, 3000);
    }
  }

  render() {
    if (this.state.hasError) {
      const isReactError130 = this.state.error?.message?.includes('Element type is invalid');

      return (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          backgroundColor: '#fff',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            maxWidth: '600px',
            padding: '40px',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            backgroundColor: '#fafafa'
          }}>
            <h1 style={{ color: '#dc3545', marginBottom: '20px' }}>
              Algo deu errado
            </h1>
            <p style={{ marginBottom: '20px' }}>
              Ocorreu um erro inesperado. Tente recarregar a página.
            </p>

            {isReactError130 && (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '20px',
                textAlign: 'left'
              }}>
                <h3 style={{ color: '#856404', margin: '0 0 10px 0' }}>
                  🔍 Erro React #130 Detectado
                </h3>
                <p style={{ color: '#856404', margin: '0' }}>
                  Este erro geralmente é causado por problemas de importação de componentes.
                  A página será recarregada automaticamente em alguns segundos.
                </p>
              </div>
            )}

            {process.env.NODE_ENV === 'development' && (
              <details style={{
                marginBottom: '20px',
                textAlign: 'left',
                backgroundColor: '#f8f9fa',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <summary style={{
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  color: '#495057'
                }}>
                  Detalhes do Erro (Desenvolvimento)
                </summary>
                <pre style={{
                  fontSize: '12px',
                  overflow: 'auto',
                  margin: '10px 0 0 0',
                  padding: '10px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px'
                }}>
                  {this.state.error?.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                Recarregar Página
              </button>

              <button
                onClick={() => {
                  this.setState({
                    hasError: false,
                    error: null,
                    errorInfo: null
                  });
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                Tentar Novamente
              </button>
            </div>

            <p style={{
              marginTop: '20px',
              fontSize: '14px',
              color: '#6c757d'
            }}>
              Tentativas de recuperação: {this.state.errorCount}/3
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Service Worker - Temporariamente desabilitado para debug
const SW_DISABLED = true; // Desabilitado para evitar conflitos durante debug

// Função para registrar o Service Worker de forma segura
const registerServiceWorker = async () => {
  if (!SW_DISABLED && 'serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    try {
      console.log('🔧 Registrando Service Worker...');

      // Aguardar um pouco para garantir que a aplicação esteja totalmente carregada
      await new Promise(resolve => setTimeout(resolve, 1000));

      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      if (registration.installing) {
        console.log('Service Worker instalando...');
      } else if (registration.waiting) {
        console.log('Service Worker esperando...');
      } else if (registration.active) {
        console.log('Service Worker ativo');
      }

      // Escutar mudanças no Service Worker com AbortController para cleanup
      const controller = new AbortController();

      registration.addEventListener('updatefound', () => {
        console.log('Nova versão do Service Worker encontrada');
      }, { signal: controller.signal });

      // Cleanup quando a página for descarregada
      window.addEventListener('beforeunload', () => {
        controller.abort();
      });

      console.log('✅ Service Worker registrado com sucesso:', registration.scope);
      return registration;
    } catch (error) {
      console.warn('⚠️ Falha ao registrar Service Worker:', error);
      // Não relançar o erro para evitar quebrar a aplicação
      return null;
    }
  } else {
    console.log('🚫 Service Worker desabilitado ou não suportado');
    return null;
  }
};

// Inicialização da aplicação
const initializeApp = async () => {
  try {
    // Registrar Service Worker primeiro
    await registerServiceWorker();

    // Renderizar aplicação
    const root = createRoot(document.getElementById('root'));
    root.render(
      <StrictMode>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </StrictMode>
    );

    console.log('✅ Aplicação inicializada com sucesso');
  } catch (error) {
    console.error('❌ Erro ao inicializar aplicação:', error);
    // Fallback para renderização direta
    document.getElementById('root').innerHTML = `
      <div style="padding: 20px; text-align: center; font-family: system-ui, -apple-system, sans-serif;">
        <h1>Erro de Inicialização</h1>
        <p>Ocorreu um erro ao carregar a aplicação. Tente recarregar a página.</p>
        <button onclick="window.location.reload()" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
          Recarregar Página
        </button>
      </div>
    `;
  }
};

// Inicializar aplicação
initializeApp();

// Tratamento global de erros não capturados
window.addEventListener('error', (event) => {
  console.error('❌ Erro global capturado:', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error
  });

  // Evitar quebra da aplicação
  event.preventDefault();
});

// Tratamento de promises rejeitadas não capturadas
window.addEventListener('unhandledrejection', (event) => {
  console.error('❌ Promise rejeitada não tratada:', event.reason);

  // Evitar que a página "quebre" por promises rejeitadas
  event.preventDefault();
});

// Verificar se é uma PWA instalada
if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
  console.log('📱 Executando como PWA');
}
